import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { api, ApiError, type ItQueueRow } from '../api';
import { Alert, Badge, Button, Card, EmptyState, PageHeader, Spinner } from '../components/ui';
import { IconCheckCircle } from '../components/icons';

/** Approved work waiting for IT. Picked up rather than assigned, same as the
 *  approval pool: whoever has time takes the next one. */
export function ItQueue() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const queue = useQuery({ queryKey: ['it-queue'], queryFn: () => api.itQueue(), refetchInterval: 30_000 });
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const assign = useMutation({
    mutationFn: (id: string) => api.assignRequest(id),
    onSuccess: (_data, id) => {
      void queryClient.invalidateQueries({ queryKey: ['it-queue'] });
      navigate(`/requests/${id}`);
    },
  });

  const assignBulk = useMutation({
    mutationFn: (ids: string[]) => api.assignRequestsBulk(ids),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['it-queue'] });
      setSelected(new Set());
    },
  });

  if (queue.isPending) return <Spinner label="Loading the IT queue" />;
  if (queue.isError) {
    return <Alert>{queue.error instanceof ApiError ? queue.error.message : 'Could not load the queue.'}</Alert>;
  }

  const rows = queue.data.requests;
  const waiting = rows.filter((row) => !row.inProgress).length;

  const toggleSelected = (id: string) => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div>
      <PageHeader
        title="IT queue"
        description="Approved requests waiting to be implemented, oldest approval first."
        actions={
          <>
            {rows.length > 0 && (
              <Badge tone={waiting > 0 ? 'warning' : 'brand'} dot>
                {waiting} to pick up · {rows.length - waiting} in progress
              </Badge>
            )}
            {selected.size > 0 && (
              <>
                <Badge tone="brand">{selected.size} selected</Badge>
                <Button
                  size="sm"
                  onClick={() => assignBulk.mutate([...selected])}
                  disabled={assignBulk.isPending}
                >
                  Pick up {selected.size}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>
                  Clear
                </Button>
              </>
            )}
          </>
        }
      />

      {assign.isError && (
        <div className="mb-4">
          <Alert>{assign.error instanceof ApiError ? assign.error.message : 'Could not pick that up.'}</Alert>
        </div>
      )}

      {assignBulk.isError && (
        <div className="mb-4">
          <Alert>{assignBulk.error instanceof ApiError ? assignBulk.error.message : 'Could not pick those up.'}</Alert>
        </div>
      )}

      {assignBulk.isSuccess && assignBulk.data.failed.length > 0 && (
        <div className="mb-4">
          <Alert tone="warning">
            {assignBulk.data.assigned.length} picked up. {assignBulk.data.failed.length} could not be: somebody
            else likely took them first. Reload the queue to see where things stand.
          </Alert>
        </div>
      )}

      {!queue.data.pdfAvailable && (
        <div className="mb-6">
          <Alert tone="warning">
            PDF export is off because no Chromium is installed on this server. Signed records still open and print
            from the browser. Install it with <code>apt-get install chromium</code> to turn PDFs on.
          </Alert>
        </div>
      )}

      {rows.length === 0 ? (
        <EmptyState
          icon={<IconCheckCircle />}
          tone="success"
          title="Queue is clear"
          body="Nothing is waiting for IT. Approved requests land here automatically."
        />
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-3xl text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50/60 text-left text-xs font-medium tracking-wider text-slate-500 uppercase dark:border-slate-800 dark:bg-slate-900/60 dark:text-slate-400">
                  <th className="w-10 px-5 py-3">
                    <span className="sr-only">Select</span>
                  </th>
                  <th className="px-5 py-3">Reference</th>
                  <th className="px-5 py-3">Employee</th>
                  <th className="px-5 py-3">Start date</th>
                  <th className="px-5 py-3">Progress</th>
                  <th className="px-5 py-3">Approved</th>
                  <th className="px-5 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <Row
                    key={row.id}
                    row={row}
                    busy={assign.isPending}
                    onPickUp={() => assign.mutate(row.id)}
                    selected={selected.has(row.id)}
                    onToggleSelected={() => toggleSelected(row.id)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

function Row({
  row,
  busy,
  onPickUp,
  selected,
  onToggleSelected,
}: {
  row: ItQueueRow;
  busy: boolean;
  onPickUp: () => void;
  selected: boolean;
  onToggleSelected: () => void;
}) {
  const urgent = row.startDate ? new Date(row.startDate).getTime() - Date.now() < 7 * 86400_000 : false;

  return (
    <tr className="border-b border-slate-100 transition-colors last:border-b-0 hover:bg-slate-50/70 dark:border-slate-800/70 dark:hover:bg-slate-800/40">
      <td className="px-5 py-3">
        {!row.inProgress && (
          <input
            type="checkbox"
            checked={selected}
            onChange={onToggleSelected}
            aria-label={`Select ${row.reference} to pick up`}
            className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500 dark:border-slate-600"
          />
        )}
      </td>
      <td className="px-5 py-3">
        <Link
          to={`/requests/${row.id}`}
          className="font-medium text-brand-700 underline-offset-4 hover:underline dark:text-brand-300"
        >
          {row.reference}
        </Link>
        {row.inProgress && (
          <div className="mt-1">
            <Badge tone="copper">{row.assignedTo === 'you' ? 'You have this' : `With ${row.assignedTo}`}</Badge>
          </div>
        )}
      </td>
      <td className="px-5 py-3 text-slate-900 dark:text-slate-100">{row.employeeName ?? 'N/A'}</td>
      <td className="px-5 py-3">
        <span className={urgent ? 'font-medium text-copper-700 dark:text-copper-400' : 'text-slate-600 dark:text-slate-400'}>
          {row.startDate ? new Date(row.startDate).toLocaleDateString(undefined, { dateStyle: 'medium' }) : 'N/A'}
        </span>
      </td>
      <td className="px-5 py-3 tabular-nums text-slate-600 dark:text-slate-400">
        {row.doneCount} / {row.itemCount}
      </td>
      <td className="px-5 py-3 text-slate-600 dark:text-slate-400">
        {row.approvedAt ? new Date(row.approvedAt).toLocaleDateString(undefined, { dateStyle: 'medium' }) : 'N/A'}
      </td>
      <td className="px-5 py-3 text-right">
        {row.inProgress ? (
          <Link
            to={`/requests/${row.id}`}
            className="text-sm font-medium text-brand-700 underline underline-offset-4 dark:text-brand-300"
          >
            Open
          </Link>
        ) : (
          <Button size="sm" onClick={onPickUp} disabled={busy}>
            Pick up
          </Button>
        )}
      </td>
    </tr>
  );
}
